import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { getImageUrl, prefetchCarImages } from '@/lib/image-utils';
import { supabase } from '@/integrations/supabase/client';

interface CarImage {
  storage_path: string;
  storage_path_avif?: string | null;
  storage_path_webp?: string | null;
  is_primary: boolean;
  position: number;
}

interface Car {
  id: string;
  brand: string;
  model: string;
  year: number;
  price: number;
  km: number;
  fuel: string;
  transmission: string;
  color: string;
  status: string;
  car_images?: CarImage[];
}

interface CarCardProps {
  car: Car;
  showSoldBadge?: boolean;
  className?: string;
}

export function CarCard({ car, showSoldBadge = false, className }: CarCardProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const prefetchCar = useCallback(() => {
    if (showSoldBadge) return;
    queryClient.prefetchQuery({
      queryKey: ['car', car.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('cars')
          .select('*, car_images(id, storage_path, storage_path_avif, storage_path_webp, is_primary, position)')
          .eq('id', car.id)
          .maybeSingle();
        if (error) throw error;
        // Prefetch gallery images into browser cache
        if (data?.car_images?.length) {
          prefetchCarImages(data.car_images);
        }
        return data;
      },
      staleTime: 30_000,
    });
  }, [car.id, showSoldBadge, queryClient]);

  // Get primary image or first image
  const primaryImage = car.car_images?.find(img => img.is_primary) || car.car_images?.[0];
  
  // Get public URLs for the image formats
  // Serve resized images (800px wide covers desktop card display at 662px + retina)
  const imgSize = { width: 800, height: 600 };
  const imageUrl = primaryImage
    ? getImageUrl(primaryImage.storage_path, imgSize)
    : null;
  const avifUrl = primaryImage?.storage_path_avif
    ? getImageUrl(primaryImage.storage_path_avif, imgSize)
    : null;
  const webpUrl = primaryImage?.storage_path_webp
    ? getImageUrl(primaryImage.storage_path_webp, imgSize)
    : null;

  // Format price
  const formattedPrice = new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(car.price);

  // Format km
  const formattedKm = new Intl.NumberFormat('pt-PT').format(car.km);

  // Get fuel type translation
  const fuelLabel = t.fuel[car.fuel as keyof typeof t.fuel] || car.fuel;

  const Wrapper = showSoldBadge ? 'div' : Link;
  const wrapperProps = showSoldBadge
    ? { className: cn('glass-card block overflow-hidden group transition-all duration-300', className) }
    : { to: `/carro/${car.id}`, onMouseEnter: prefetchCar, onTouchStart: prefetchCar, className: cn('glass-card block overflow-hidden group transition-all duration-300 hover:scale-[1.02]', className) };

  return (
    <Wrapper {...(wrapperProps as any)}>
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {imageUrl ? (
          <picture>
            {avifUrl && <source srcSet={`${avifUrl} 800w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" type="image/avif" />}
            {webpUrl && <source srcSet={`${webpUrl} 800w`} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" type="image/webp" />}
            <img
              src={imageUrl}
              srcSet={`${imageUrl} 800w`}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              alt={`${car.brand} ${car.model}`}
              width={800}
              height={600}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          </picture>
        ) : (
          <div className="w-full h-full bg-secondary flex items-center justify-center">
            <span className="text-muted-foreground text-sm">Sem imagem</span>
          </div>
        )}

        {/* Sold badge */}
        {showSoldBadge && car.status === 'sold' && (
          <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
            <Badge className="bg-primary text-primary-foreground text-lg px-4 py-2 font-bold">
              {t.recentlySold.sold}
            </Badge>
          </div>
        )}

        {/* Price badge - hidden for sold cars */}
        {!showSoldBadge && (
          <div className="absolute bottom-3 right-3">
            <Badge className="bg-primary text-primary-foreground text-base px-3 py-1.5 font-bold">
              {formattedPrice}
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Title */}
        <h3 className="font-semibold text-lg truncate">
          {car.brand} {car.model}
        </h3>

        {/* Specs */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary" className="text-xs">
            {car.year}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {formattedKm} km
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {fuelLabel}
          </Badge>
        </div>
      </div>
    </Wrapper>
  );
}
